<template>
    <div class="bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />
        <h2 class="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Laporan Template Dokumen</h2>

        <div class="bg-gray-50 p-4 rounded-lg border mb-6">
            <div class="flex flex-wrap items-end gap-4">
                <div class="flex-grow">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="select-customer">
                        Customer Name
                    </label>
                    <select id="select-customer" v-model="selectedCustomerId" :disabled="isAllCustomers"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:bg-gray-200 disabled:cursor-not-allowed">
                        <option :value="null" disabled>-- Pilih Customer --</option>
                        <option v-for="cust in customerList" :key="cust.id" :value="cust.id">
                            {{ cust.name }}
                        </option>
                    </select>
                </div>
                <div class="flex items-center pb-2">
                    <label class="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" v-model="isAllCustomers"
                            class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500">
                        <span class="text-gray-700 text-sm font-bold">ALL</span>
                    </label>
                </div>
                <div class="flex items-center space-x-2 pb-1">
                    <button @click="previewReport" class="button-primary" :disabled="isLoading">
                        <span v-if="!isLoading">Preview</span>
                        <span v-else class="flex items-center">
                            <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                                fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                            Loading...
                        </span>
                    </button>
                    <button @click="exportToExcel" :disabled="!reportData.length || isLoading" class="button-secondary">
                        Export to Excel
                    </button>
                </div>
            </div>
        </div>

        <div class="overflow-x-auto">
            <table class="min-w-full bg-white">
                <thead class="bg-gray-100">
                    <tr>
                        <th v-for="header in tableHeaders" :key="header" class="table-header">
                            {{ header }}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-if="isLoading">
                        <td :colspan="tableHeaders.length" class="text-center p-8 text-gray-500">
                            Sedang memuat data...
                        </td>
                    </tr>

                    <tr v-else-if="reportData.length === 0">
                        <td :colspan="tableHeaders.length" class="text-center p-8 text-gray-500">
                            Tidak ada data untuk ditampilkan. Silakan klik "Preview".
                        </td>
                    </tr>

                    <template v-else>
                        <tr v-for="(row, index) in reportData" :key="row.id" class="hover:bg-gray-100 even:bg-gray-50">
                            <td v-for="header in tableHeaders" :key="header" class="table-cell"
                                :class="{ 'text-center': isCentered(header, row) }">
                                <span v-if="header === 'No'">
                                    {{ index + 1 }}
                                </span>
                                <span v-else-if="typeof row[header] === 'boolean'">
                                    <span v-if="row[header]" class="text-green-600 font-bold">✓</span>
                                </span>
                                <span v-else>
                                    {{ row[header] }}
                                </span>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
// Bagian <script> tidak berubah sama sekali
import axios from 'axios';
import * as XLSX from 'xlsx';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

const API_URL = '/api/templatereport';

export default {
    name: 'TemplateReport',
    components: { MonkeyLoader },
    data() {
        return {
            allReportData: [],
            reportData: [],
            selectedCustomerId: null,
            isAllCustomers: true,
            isLoading: false,
        };
    },
    computed: {
        customerList() {
            if (!this.allReportData.length) return [];
            const customerMap = new Map();
            this.allReportData.forEach(row => {
                if (row.custId && !customerMap.has(row.custId)) {
                    customerMap.set(row.custId, { id: row.custId, name: row.CUSTOMER });
                }
            });
            return Array.from(customerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        },
        tableHeaders() {
            if (this.reportData.length === 0 && !this.isLoading) {
                return ['No', 'Kategory Company', 'CUSTOMER', 'Type PO', 'Template'];
            }
            if (this.allReportData.length > 0) {
                const headers = Object.keys(this.allReportData[0]).filter(key => key !== 'id' && key !== 'custId' && key !== 'No');
                return ['No', ...headers];
            }
            return ['No', 'Kategory Company', 'CUSTOMER', 'Type PO', 'Template'];
        },
    },
    methods: {
        isCentered(header, row) {
            return typeof row[header] === 'boolean' || header === 'No';
        },
        async fetchInitialData() {
            this.isLoading = true;
            try {
                const response = await axios.get(API_URL);
                this.allReportData = response.data;
                this.reportData = this.allReportData;
            } catch (error) {
                console.error("Gagal memuat data laporan awal:", error);
                alert('Gagal memuat data laporan dari server.');
            } finally {
                this.isLoading = false;
            }
        },
        previewReport() {
            if (this.isAllCustomers) {
                this.reportData = this.allReportData;
            } else {
                if (!this.selectedCustomerId) {
                    alert("Silakan pilih customer terlebih dahulu atau centang 'ALL'.");
                    return;
                }
                this.reportData = this.allReportData.filter(row => row.custId === this.selectedCustomerId);
            }
        },
        exportToExcel() {
            if (this.reportData.length === 0) {
                alert("Tidak ada data untuk diexport. Klik 'Preview' terlebih dahulu.");
                return;
            }
            const dataToExport = this.reportData.map((row, index) => {
                const newRow = {};
                this.tableHeaders.forEach(header => {
                    if (header === 'No') {
                        newRow[header] = index + 1;
                    } else {
                        let value = row[header];
                        if (typeof value === 'boolean') {
                            value = value ? '✓' : '';
                        }
                        newRow[header] = value;
                    }
                });
                return newRow;
            });
            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Template");
            const columnWidths = this.tableHeaders.map(key => ({
                wch: key.length < 15 ? 15 : key.length + 2
            }));
            worksheet["!cols"] = columnWidths;
            XLSX.writeFile(workbook, "Laporan_Template_Dokumen.xlsx");
        }
    },
    mounted() {
        this.fetchInitialData();
    }
};
</script>

<style scoped>
/* Style tidak berubah */
.input-border {
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.input-border:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
}

.button-primary {
    @apply bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:bg-gray-400;
}

.button-secondary {
    @apply bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed;
}

.table-header {
    @apply p-3 font-bold text-left text-sm bg-gray-200 text-gray-700 uppercase tracking-wider sticky top-0 z-10;
}

.table-cell {
    @apply p-3 text-sm text-gray-800 border-b border-gray-200;
}

/* Kita hapus sementara CSS transisi untuk memastikan struktur benar */
</style>